// Error reporting utility for centralized error handling

interface ErrorReport {
    message: string;
    stack?: string;
    componentStack?: string;
    timestamp: string;
    userAgent: string;
    url: string;
    userId?: string;
    sessionId?: string;
}

class ErrorReporter {
    private static instance: ErrorReporter;
    private errorQueue: ErrorReport[] = [];
    private isOnline: boolean = navigator.onLine;

    private constructor() {
        // Listen for online/offline events
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.flushErrorQueue();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
        });
    }

    public static getInstance(): ErrorReporter {
        if (!ErrorReporter.instance) {
            ErrorReporter.instance = new ErrorReporter();
        }
        return ErrorReporter.instance;
    }

    public reportError(error: Error, errorInfo?: any, userId?: string): void {
        const errorReport: ErrorReport = {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo?.componentStack,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
            userId,
            sessionId: this.getSessionId(),
        };

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('Error Report:', errorReport);
        }

        // Store in localStorage for persistence
        this.storeErrorLocally(errorReport);

        // Send to server if online
        if (this.isOnline) {
            this.sendErrorToServer(errorReport);
        } else {
            // Queue for later if offline
            this.errorQueue.push(errorReport);
        }
    }

    private storeErrorLocally(errorReport: ErrorReport): void {
        try {
            const storedErrors = this.getStoredErrors();
            storedErrors.push(errorReport);

            // Keep only last 50 errors to prevent localStorage bloat
            if (storedErrors.length > 50) {
                storedErrors.splice(0, storedErrors.length - 50);
            }

            localStorage.setItem('errorReports', JSON.stringify(storedErrors));
        } catch (e) {
            console.warn('Failed to store error locally:', e);
        }
    }

    private getStoredErrors(): ErrorReport[] {
        try {
            const stored = localStorage.getItem('errorReports');
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            return [];
        }
    }

    private async sendErrorToServer(errorReport: ErrorReport): Promise<void> {
        try {
            // You can replace this with your actual error reporting endpoint
            const response = await fetch('/api/errors', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(errorReport),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (e) {
            console.warn('Failed to send error to server:', e);
            // Add back to queue if sending failed
            this.errorQueue.push(errorReport);
        }
    }

    private async flushErrorQueue(): Promise<void> {
        if (this.errorQueue.length === 0) return;

        const errorsToSend = [...this.errorQueue];
        this.errorQueue = [];

        for (const errorReport of errorsToSend) {
            await this.sendErrorToServer(errorReport);
        }
    }

    private getSessionId(): string {
        let sessionId = sessionStorage.getItem('sessionId');
        if (!sessionId) {
            sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
            sessionStorage.setItem('sessionId', sessionId);
        }
        return sessionId;
    }

    public getStoredErrorCount(): number {
        return this.getStoredErrors().length;
    }

    public clearStoredErrors(): void {
        localStorage.removeItem('errorReports');
    }
}

// Export singleton instance
export const errorReporter = ErrorReporter.getInstance();

// Convenience function for easy error reporting
export const reportError = (error: Error, errorInfo?: any, userId?: string): void => {
    errorReporter.reportError(error, errorInfo, userId);
};

// Hook for React components to report errors
export const useErrorReporting = () => {
    return {
        reportError: (error: Error, errorInfo?: any) => {
            errorReporter.reportError(error, errorInfo);
        },
        getErrorCount: () => errorReporter.getStoredErrorCount(),
        clearErrors: () => errorReporter.clearStoredErrors(),
    };
};
